// User display lookup: turns a user id (ULID) into the friendly username for display.
//
// FAST keeps the ULID for referential integrity and sends it to the WMS; the WMS knows nothing about FAST usernames.
// Wherever a grid or form shows a WMS user (picker, assigned to, created by...), the Model marks the field
// with referenceEntity 'User' and the widget resolves the value here. Values that are not a known user id
// (older rows holding an email or a name, SYSTEM, FAST_SYSTEM) are shown exactly as stored.
//
// Data comes from a script (user_username_options), like every other list in the UI. The directory reads the users
// at display time, so nothing has to be copied when a user registers and a renamed user shows the new name everywhere.
import { useEffect, useState } from 'react';
import FlexService from './FlexService';

const SCRIPT = 'user_username_options';
const TTL_MS = 5 * 60 * 1000;

let directory = new Map();      // user id -> username
let loadedAt = 0;
let inflight = null;
let version = 0;
const listeners = new Set();

const notify = () => { version += 1; listeners.forEach((fn) => fn(version)); };

export const resolveUser = (id) => {
  if (id === null || id === undefined || id === '') return null;
  return directory.get(String(id)) || null;
};

export const displayUser = (id) => resolveUser(id) || (id === null || id === undefined ? '' : String(id));

export const loadUserDirectory = (force = false) => {
  const fresh = Date.now() - loadedAt < TTL_MS;
  if (!force && fresh) return Promise.resolve(directory);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await FlexService.executeScript({ scriptName: SCRIPT, context: {} });
      const list = res?.context?.result;
      if (res?.success && Array.isArray(list)) {
        const next = new Map();
        list.forEach((o) => { if (o && o.value) next.set(String(o.value), String(o.label || o.value)); });
        directory = next;
        loadedAt = Date.now();
        notify();
      }
    } catch (err) {
      console.error('Failed to load the user directory:', err);
    } finally {
      inflight = null;
    }
    return directory;
  })();
  return inflight;
};

/** Forget the cache (for example after signing in as someone else). */
export const resetUserDirectory = () => { directory = new Map(); loadedAt = 0; notify(); };

/**
 * Loads the directory when `enabled` and re-renders the caller when it arrives. Returns the current version,
 * which a memo can list as a dependency so that sorting and filtering by display name update too.
 */
export const useUserDirectory = (enabled = true) => {
  const [v, setV] = useState(version);
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setV);
    loadUserDirectory();
    return () => { listeners.delete(setV); };
  }, [enabled]);
  return v;
};
